import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/MainContainer.css';
import './EventAnnouncement.css';
import api from '../wrapper/API';
import { useSuccessBar } from '../context/SuccessBarContext';
import { usePopup } from '../context/PopupContext';
import Popup from '../popup/Popup';
import CustomDropdown from '../input/CustomDropdown';
import ActionButton from '../input/ActionButton';
import CustomTogglebox from '../input/CustomTogglebox';
import Icon from '../home/Icon';
import OfflineBanner from '../shell/OfflineBanner';
import EventProcessStepper from '../status/EventProcessStepper';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import useLocations from '../../hooks/useLocations';
import { invalidateAddOns } from '../../hooks/useAddOns';
import NewProcessPopup, { NEW_PROCESS_POPUP } from '../process/NewProcessPopup';
import AddEventPopup, { ADD_EVENT_POPUP } from '../process/AddEventPopup';
import ProcessMeta from '../process/ProcessMeta';
import ProcessFlatRates from '../process/ProcessFlatRates';
import ProcessAddOns from '../process/ProcessAddOns';
import ProcessEventTable from '../process/ProcessEventTable';
import ProcessStepsEditor from '../process/ProcessStepsEditor';
import StepPanel from '../process/StepPanel';
import { apiErrorMessage, currentStepKey, eventNouns, summaryFromDetail } from '../process/processUtils';

const ERROR_POPUP = 'eventprocesserror';
const DELETE_POPUP = 'processdelete';

/**
 * "Event Prozesse" (/eventannouncement): processes created from a process template run their typed
 * steps (AssignLeaders, Release, SendMail, ManualCheck) on the server-side step engine. The page holds
 * the selected process (EventProcessResponse — every mutation returns it, so it is patched locally
 * instead of refetched) and which step is viewed; the parts live in components/process/.
 */
function EventAnnouncement() {
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const { openPopup } = usePopup();
  const { active: activeGroups, byId: groupsById } = useParticipantGroups();
  // One location list for both popups ("Neuer Prozess", "+ Event"), fetched lazily on first need.
  const locationsApi = useLocations();

  // ── Processes ────────────────────────────────────────────────────────────────────────────────
  const [processes, setProcesses] = useState([]);       // EventProcessSummary[]
  const [selectedProcessId, setSelectedProcessId] = useState('');
  // Mirrors selectedProcessId; selectProcess sets it eagerly, so late responses of the previously
  // selected process are recognized and ignored (applyProcess).
  const selectedIdRef = useRef('');
  selectedIdRef.current = selectedProcessId;
  const [process, setProcess] = useState(null);         // EventProcessResponse | null
  const processRef = useRef(null);
  processRef.current = process;
  const detailRequestRef = useRef(0);

  const selectProcess = useCallback((id) => {
    selectedIdRef.current = id;
    setSelectedProcessId(id);
  }, []);

  // Templates incl. archived (header chip + autopay default of running processes); cards show the
  // active ones. Fetched once.
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  // ── Step navigation (pure UI, independent of the backend status) ─────────────────────────────
  // viewedStepKey = key of the shown step; null = follow the current step (first pending one).
  const [viewedStepKey, setViewedStepKey] = useState(null);
  const [editingSteps, setEditingSteps] = useState(false);

  // ── Lazily loaded reference data (once per page) ─────────────────────────────────────────────
  const [leaderCandidates, setLeaderCandidates] = useState(null);
  const [textblocks, setTextblocks] = useState(null);
  const leadersRequestedRef = useRef(false);
  const textblocksRequestedRef = useRef(false);

  const [topError, setTopError] = useState('');
  const [deleteRevoke, setDeleteRevoke] = useState(false);

  const reportError = useCallback((message) => {
    setTopError(message);
    openPopup(ERROR_POPUP, 1);
  }, [openPopup]);

  // Takes over a process response (all mutations return the full process) and keeps the selection
  // list in sync without GetAll. resetView (default): the view jumps to the current step; false: the
  // step on screen stays, even if it was only shown as the current one (e.g. a mail step that got a
  // 409, or a run with warnings to read — otherwise the panel would unmount and lose them).
  const applyProcess = useCallback((detail, { resetView = true } = {}) => {
    if (!detail) return;
    setProcesses(prev => prev.map(p => (p.id === detail.id ? summaryFromDetail(detail, p) : p)));
    // Late response of a process that is no longer selected (also "– Prozess wählen –"): only its
    // list entry is updated.
    if (String(detail.id) !== String(selectedIdRef.current)) return;
    if (resetView) {
      setViewedStepKey(null);
    } else {
      // Computed from the process on screen before it is replaced (processRef changes on render).
      const shownProcess = processRef.current;
      if (shownProcess && String(shownProcess.id) === String(detail.id)) {
        const shownSteps = shownProcess.steps ?? [];
        const shownCurrentKey = currentStepKey(shownProcess);
        setViewedStepKey(prev => (prev && shownSteps.some(s => s.key === prev) ? prev : shownCurrentKey));
      }
    }
    setProcess(detail);
  }, []);

  const loadProcessDetail = useCallback((id) => {
    const request = ++detailRequestRef.current;
    if (!id) {
      setProcess(null);
      return Promise.resolve(null);
    }
    return api.get(`/EventProcess/GetById?id=${id}`)
      .then(response => {
        if (request === detailRequestRef.current) applyProcess(response.data);
        return response.data;
      })
      .catch(() => {
        if (request === detailRequestRef.current) setProcess(null);
        return null;
      });
  }, [applyProcess]);

  useEffect(() => {
    api.get('/EventProcess/GetAll')
      .then(response => {
        const list = Array.isArray(response.data) ? response.data : [];
        setProcesses(list);
        setSelectedProcessId(prev => prev || (list.length > 0 ? String(list[0].id) : ''));
      })
      .catch(() => setProcesses([]));
    api.get('/ProcessTemplate/GetAll?includeArchived=true')
      .then(response => setTemplates(Array.isArray(response.data) ? response.data : []))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoaded(true));
  }, []);

  // Load the detail when the selection changes (not when it is already there, e.g. right after Create).
  useEffect(() => {
    setViewedStepKey(null);
    setEditingSteps(false);
    if (selectedProcessId && processRef.current && String(processRef.current.id) === String(selectedProcessId)) return;
    loadProcessDetail(selectedProcessId);
  }, [selectedProcessId, loadProcessDetail]);

  // Re-read the process after a conflict (409) without moving the view.
  const reloadProcess = useCallback(() => {
    const id = processRef.current?.id;
    if (!id) return Promise.resolve(null);
    return api.get(`/EventProcess/GetById?id=${id}`)
      .then(response => {
        applyProcess(response.data, { resetView: false });
        return response.data;
      })
      .catch(() => null);
  }, [applyProcess]);

  const ensureLeaderCandidates = useCallback(() => {
    if (leadersRequestedRef.current) return;
    leadersRequestedRef.current = true;
    api.get('/EventProcess/LeaderCandidates')
      .then(r => setLeaderCandidates(Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        leadersRequestedRef.current = false;
        setLeaderCandidates([]);
      });
  }, []);

  const ensureTextblocks = useCallback(() => {
    if (textblocksRequestedRef.current) return;
    textblocksRequestedRef.current = true;
    api.get('/Textblock/GetAllNames?language=DE')
      // System blocks (2FA, newsletter confirmations, orga welcome) are never assignable to mail steps.
      .then(r => setTextblocks((Array.isArray(r.data) ? r.data : []).filter(tb => !tb.isSystemBlock)))
      .catch(() => {
        textblocksRequestedRef.current = false;
        setTextblocks([]);
      });
  }, []);

  // ── Create / delete ──────────────────────────────────────────────────────────────────────────
  const handleCreated = (detail) => {
    setProcesses(prev => [summaryFromDetail(detail), ...prev.filter(p => p.id !== detail.id)]);
    // Select first: applyProcess ignores responses of a process that is not selected.
    selectProcess(String(detail.id));
    applyProcess(detail);
  };

  const openDeletePopup = () => {
    if (!process) return;
    // Default as before: never-public processes take their releases back (members would otherwise
    // keep seeing private events without the inviting process).
    setDeleteRevoke(!!process.neverPublic);
    openPopup(DELETE_POPUP, process.id);
  };

  const handleDeleteProcess = () => {
    if (!process) return;
    const id = process.id;
    startSuccessBarLoading('Prozess wird gelöscht');
    api.delete(`/EventProcess/${id}?revokeReleases=${deleteRevoke}`)
      .then(() => {
        // Its add-on rows are gone — the catalog's usage counts are refetched on its next load.
        invalidateAddOns();
        startSuccessBarSuccess('Prozess gelöscht');
        const remaining = processes.filter(p => p.id !== id);
        setProcesses(remaining);
        setProcess(null);
        selectProcess(remaining.length > 0 ? String(remaining[0].id) : '');
      })
      .catch(err => {
        closeSuccessBar();
        reportError(apiErrorMessage(err, 'Prozess konnte nicht gelöscht werden.'));
      });
  };

  // ── Steps ────────────────────────────────────────────────────────────────────────────────────
  const steps = process?.steps ?? [];
  const currentKey = process ? currentStepKey(process) : null;
  const effectiveViewedKey = (viewedStepKey && steps.some(s => s.key === viewedStepKey)) ? viewedStepKey : currentKey;
  const viewedIndex = steps.findIndex(s => s.key === effectiveViewedKey);
  const viewedStep = viewedIndex >= 0 ? steps[viewedIndex] : null;

  const navigateStep = useCallback((key) => {
    setViewedStepKey(key);
  }, []);

  const navigateBy = (delta) => {
    const target = steps[viewedIndex + delta];
    if (target) navigateStep(target.key);
  };

  const template = process ? templates.find(t => t.id === process.processTemplateId) ?? null : null;
  // Detaching keeps bookings and prices, but the events lose the process add-ons (R10b) — named in the dialog.
  const deleteAddOnNames = (process?.addOns ?? []).map(a => a.addOnName).filter(Boolean).join(', ');
  const activeTemplates = templates.filter(t => !t.isArchived);
  const readOnly = !process || process.closed;

  return (
    <React.Fragment>
      <main className={'wideMain'}>
        <OfflineBanner />
        <div className={'processPage'}>
          {/* Top bar: process selection + new / delete */}
          <div className={'processTopBar'}>
            <div className={'processSelectRow'}>
              <CustomDropdown icon={'checklist'} value={selectedProcessId} onChange={e => selectProcess(e.target.value)}>
                <option value=''>– Prozess wählen –</option>
                {processes.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}{p.locationName ? ` · ${p.locationName}` : ''}{p.processTemplateName ? ` · ${p.processTemplateName}` : ''}{p.closed ? ' (abgeschlossen)' : ''}
                  </option>
                ))}
              </CustomDropdown>
              <ActionButton icon={'add'} label={'Neuer Prozess'} type={3} onClick={() => openPopup(NEW_PROCESS_POPUP, 1)} />
            </div>
            {process && (
              <ActionButton icon={'delete'} label={'Prozess löschen'} type={1} onClick={openDeletePopup} />
            )}
          </div>

          {process ? (
            <React.Fragment>
              {process.closed && (
                <div className={'processClosedBanner'}>
                  <Icon icon={'lock'} className={'processClosedIcon'} />
                  <span>Dieser Prozess ist abgeschlossen und schreibgeschützt – nur Pauschalen &amp; Abzüge sowie Zusätze lassen sich noch anpassen.</span>
                </div>
              )}

              <ProcessMeta process={process} template={template} groupsById={groupsById} />

              {/* Editable also on closed processes (statistics read the rows live); the step view stays. */}
              <ProcessFlatRates process={process} onSaved={detail => applyProcess(detail, { resetView: false })} />

              {/* Sign-up add-ons of all events of the process; editable also on closed processes (nothing is
                  repriced there). */}
              <ProcessAddOns process={process} onSaved={detail => applyProcess(detail, { resetView: false })} />

              {/* Events above the steps: the stepper, Zurück / Weiter and the step panel stay together. */}
              <ProcessEventTable
                process={process}
                groupsById={groupsById}
                readOnly={readOnly}
                onAdd={() => openPopup(ADD_EVENT_POPUP, process.id)}
                onNavigateStep={editingSteps ? undefined : navigateStep}
                onProcess={applyProcess}
                reportError={reportError}
              />

              <div className={'processStepsSection'}>
                <div className={'processSectionHeader'}>
                  <label className={'innerContainerLabel'}>Ablauf</label>
                  {!readOnly && !editingSteps && (
                    <ActionButton icon={'tune'} label={'Schritte anpassen'} type={3} onClick={() => setEditingSteps(true)} />
                  )}
                </div>
                {editingSteps ? (
                  <ProcessStepsEditor
                    process={process}
                    groups={activeGroups}
                    groupsById={groupsById}
                    textblocks={textblocks}
                    ensureTextblocks={ensureTextblocks}
                    onSaved={detail => { setEditingSteps(false); applyProcess(detail); }}
                    onCancel={() => setEditingSteps(false)}
                  />
                ) : (
                  <EventProcessStepper
                    steps={steps}
                    currentKey={currentKey}
                    viewedKey={effectiveViewedKey}
                    onStepNavigate={readOnly ? undefined : navigateStep}
                    interactive={!readOnly}
                  />
                )}
              </div>

              {!readOnly && !editingSteps && viewedStep && (
                <React.Fragment>
                  <div className={'processStepNav'}>
                    <button
                      type={'button'}
                      className={'processBackLink'}
                      onClick={() => navigateBy(-1)}
                      disabled={viewedIndex <= 0}
                    >
                      <Icon icon={'arrow_back'} className={'processBackIcon'} />
                      Zurück
                    </button>
                    <span className={'processStepNavLabel'}>
                      Schritt {viewedIndex + 1} von {steps.length} · {viewedStep.label}
                    </span>
                    <button
                      type={'button'}
                      className={'processBackLink processStepNavNext'}
                      onClick={() => navigateBy(1)}
                      disabled={viewedIndex >= steps.length - 1}
                    >
                      Weiter
                      <Icon icon={'arrow_forward'} className={'processBackIcon'} />
                    </button>
                  </div>
                  <StepPanel
                    key={`${process.id}:${viewedStep.key}`}
                    process={process}
                    step={viewedStep}
                    isCurrent={viewedStep.key === currentKey}
                    groupsById={groupsById}
                    leaderCandidates={leaderCandidates}
                    ensureLeaderCandidates={ensureLeaderCandidates}
                    onProcess={applyProcess}
                    onReload={reloadProcess}
                    reportError={reportError}
                    onNavigateStep={navigateStep}
                  />
                </React.Fragment>
              )}
            </React.Fragment>
          ) : (
            <div className={'processEmptyState'}>
              <Icon icon={'campaign'} className={'processEmptyIcon'} />
              <p>
                Wähle einen Prozess aus oder lege einen neuen an: Spieltagleitung zuweisen, Events für Gruppen
                freigeben und Ankündigungen versenden – Schritt für Schritt nach einer Vorlage.
              </p>
            </div>
          )}
        </div>
      </main>

      <NewProcessPopup
        templates={activeTemplates}
        templatesLoaded={templatesLoaded}
        locationsApi={locationsApi}
        onCreated={handleCreated}
      />
      <AddEventPopup process={process} template={template} onProcess={applyProcess} />

      <Popup name={DELETE_POPUP} confirmText={'Löschen'} headerText={'Prozess löschen'} type={'confirmaction'} onConfirm={handleDeleteProcess} withCancel>
        <p>
          Der Prozess <b>{process?.displayName}</b> wird gelöscht. Die zugeordneten Events bleiben erhalten
          (Anmeldungen, Spieltagleitung und Mail-Verlauf unverändert) und können einem neuen Prozess zugeordnet werden.
        </p>
        {deleteAddOnNames && (
          <p className={'formFieldHint'}>
            Anmelde-Zusätze des Prozesses ({deleteAddOnNames}) sind für die {eventNouns(process).many} danach nicht
            mehr buchbar; bestehende Buchungen bleiben.
          </p>
        )}
        <CustomTogglebox
          placeholder={'Freigaben zurücknehmen'}
          checked={deleteRevoke}
          onChange={e => setDeleteRevoke(e.target.checked)}
        />
        <p className={'formFieldHint'}>
          {deleteRevoke
            ? 'Die Freigaben aus diesem Prozess werden entfernt – die Events sind danach nur noch für Gruppen sichtbar, die im Bearbeiten-Popup manuell freigegeben sind.'
            : 'Die Freigaben bleiben bestehen und gelten danach als manuelle Freigaben (im Bearbeiten-Popup des Events änderbar).'}
        </p>
      </Popup>
      <Popup name={ERROR_POPUP} confirmText={'Ok'} headerText={'Fehler'} type={'error'}>
        <p>{topError}</p>
      </Popup>
    </React.Fragment>
  );
}

export default EventAnnouncement;
